/**
 * THE one safe markdown renderer for model output (owner, 2026-09-23).
 *
 * Replaces the two near-duplicate renderers in components/ui/MarkdownMessage.tsx
 * (chat) and components/ui/MarkdownAnalysis.tsx (#403 analysis) plus the
 * hand-rolled ones. The React wrapper is components/ui/Markdown.tsx; its
 * stylesheet is styles/markdown.css.
 *
 * LLM output is untrusted (it is steered by transcripts, uploaded files and
 * runbook text, any of which can carry a prompt injection), so:
 *   - raw HTML in the source is ESCAPED to visible text, never parsed;
 *   - link hrefs are allowlisted: http(s), mailto, and the two in-page anchor
 *     forms the app handles itself (`#transcript:<bubble>`, `#citation:<key>`);
 *   - images never fetch: an allowed-scheme image becomes a link carrying its
 *     alt text, anything else shows the alt text alone;
 *   - when a DOM exists, DOMPurify runs as a SECOND line of defense. For safe
 *     output it removes nothing and the renderer's own string is returned, so
 *     the server (no DOM) and the client produce byte-identical HTML — no
 *     hydration mismatch. If it ever removes something, the renderer's escaping
 *     failed: the sanitized output is used and the event is logged at ERROR.
 *
 * Content is rendered WHOLE (CLAUDE.md "No arbitrary string lengths or silent
 * content loss"): no slicing, no caps. Two `marked` behaviours that drop text
 * silently are switched off:
 *   - reference definitions (`[1]: http://…`) render as NOTHING in marked; here
 *     they stay visible as ordinary text (so `[text][1]` reference links are
 *     shown literally rather than resolved — visible beats invisible);
 *   - raw HTML/comments are escaped, so `<!-- … -->` is shown, not swallowed.
 *
 * A PRIVATE `Marked` instance is used: lib/reports/export/markdownRenderer.ts
 * calls `marked.setOptions(...)` on the GLOBAL singleton at import, which used
 * to change options for every other `marked` user once it loaded.
 */
import DOMPurify from 'dompurify';
import { Marked, Renderer, type Tokens } from 'marked';

import { escapeHtml } from '@/lib/escapeHtml';
import logger from '@/lib/logger';

/** In-page anchor prefix for transcript citations (clicks handled by ChatTab / CaptionOverlay delegation). */
export const TRANSCRIPT_HREF_PREFIX = '#transcript:';
/** In-page anchor prefix for citation chips (clicks handled by <Markdown onCitationClick>). */
export const CITATION_HREF_PREFIX = '#citation:';

// Bubble ids are URL-safe since 2026-09-24 (lib/transcriptBubbleId.ts), but ids
// persisted before then are `bubble-<raw identity>-<ms>` and every identity has
// a space ("Officer Kelly__off_8xlk"). The custom `link` renderer below receives
// marked's RAW destination (only marked's default renderer percent-encodes), so
// a space must be accepted here or those stored citations lose their link.
// Quotes, angle brackets, backticks and control characters are refused; the id
// is attribute-escaped anyway.
const TRANSCRIPT_ID_RE = /^[^"'<>`\x00-\x1F\x7F]+$/;
/** Citation keys: an alphabet that is safe unescaped in a markdown link destination and an HTML attribute. */
const CITATION_KEY_RE = /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/;

type SafeHref =
  | { kind: 'external'; href: string }
  | { kind: 'transcript'; href: string; bubbleId: string }
  | { kind: 'citation'; href: string; key: string };

function classifyHref(href?: string | null): SafeHref | null {
  if (!href) return null;
  const trimmed = href.trim();
  // Allowlist, not a blocklist: javascript:, data:, vbscript:, entity-encoded
  // variants and every other scheme fail to match and lose their href.
  if (/^(https?:|mailto:)/i.test(trimmed)) return { kind: 'external', href: trimmed };
  if (trimmed.startsWith(TRANSCRIPT_HREF_PREFIX)) {
    const bubbleId = trimmed.slice(TRANSCRIPT_HREF_PREFIX.length);
    if (TRANSCRIPT_ID_RE.test(bubbleId)) return { kind: 'transcript', href: trimmed, bubbleId };
    return null;
  }
  if (trimmed.startsWith(CITATION_HREF_PREFIX)) {
    const key = trimmed.slice(CITATION_HREF_PREFIX.length);
    if (CITATION_KEY_RE.test(key)) return { kind: 'citation', href: trimmed, key };
    return null;
  }
  return null;
}

// marked 14 already HTML-escapes link/image `title` and image `text` in its
// tokenizer (outputLink → escape()); escaping again would show `&amp;quot;`.
const titleAttr = (title?: string | null) => (title ? ` title="${title}"` : '');

const md = new Marked({ gfm: true, breaks: true, async: false });

md.use({
  tokenizer: {
    // Returning undefined (not false) disables the definition tokenizer, so the
    // line falls through to a paragraph and stays visible. See header.
    def() {
      return undefined;
    },
  },
  renderer: {
    code({ text, lang }: Tokens.Code) {
      const language = (lang || '').match(/^\S*/)?.[0];
      const cls = language ? ` class="language-${escapeHtml(language)}"` : '';
      return `<pre><code${cls}>${escapeHtml(text)}\n</code></pre>\n`;
    },

    // Raw HTML in the markdown source → literal visible text. This is the key
    // XSS closer: anything between `<` and `>` is never parsed as an element.
    html({ text }: Tokens.HTML | Tokens.Tag) {
      return escapeHtml(text);
    },

    link(this: Renderer, { href, title, tokens }: Tokens.Link) {
      const inner = this.parser.parseInline(tokens);
      const safe = classifyHref(href);
      if (!safe) return inner;
      if (safe.kind === 'transcript') {
        // In-page — no target=_blank. Attribute order is pinned by tests and
        // read by the ChatTab / CaptionOverlay click delegation.
        return `<a href="${escapeHtml(safe.href)}" data-transcript-bubble="${escapeHtml(safe.bubbleId)}" class="transcript-citation"${titleAttr(title)}>${inner}</a>`;
      }
      if (safe.kind === 'citation') {
        return `<a href="${escapeHtml(safe.href)}" data-citation="${escapeHtml(safe.key)}" class="app-markdown-citation"${titleAttr(title)}>${inner}</a>`;
      }
      return `<a href="${escapeHtml(safe.href)}"${titleAttr(title)} target="_blank" rel="noopener noreferrer">${inner}</a>`;
    },

    // Images never load (no remote fetch = no tracking/exfiltration beacon).
    // An allowed-scheme image keeps its destination as a link, so neither the
    // alt text nor the URL is lost; anything else shows the alt text.
    image({ href, title, text }: Tokens.Image) {
      const safe = classifyHref(href);
      const label = text || (safe?.kind === 'external' ? escapeHtml(safe.href) : ''); // text: pre-escaped by marked
      if (safe?.kind === 'external') {
        return `<a href="${escapeHtml(safe.href)}"${titleAttr(title)} target="_blank" rel="noopener noreferrer">${label}</a>`;
      }
      return label;
    },

    // Wrap tables so a wide one scrolls inside its own block instead of
    // overflowing (or being clipped by) the container.
    table(this: Renderer, token: Tokens.Table) {
      return `<div class="app-markdown-table">${Renderer.prototype.table.call(this, token)}</div>\n`;
    },
  },
});

/**
 * Normalize Anderra responses before parsing so the transcript-citation
 * pattern [quoted](#transcript:bubble-id) survives common LLM output quirks
 * that otherwise render as raw text in chat ("bleed-through"). See
 * docs/action-pipeline-redesign/17-side-by-side-testing-guide.md for the
 * full list of patterns covered. (Moved verbatim from MarkdownMessage.tsx.)
 */
export function preprocessAnderraCitations(content: string): string {
  let out = content;

  // 1. Drop a single pair of surrounding backticks around a transcript link:
  //    `[text](#transcript:bubble-id)` → [text](#transcript:bubble-id)
  out = out.replace(
    /`(\[[^\]\n]+\]\(#transcript:[^)\n`]+\))`/g,
    '$1',
  );

  // 2. Drop backslash escapes in front of the link brackets/parens:
  //    \[text\](#transcript:id) → [text](#transcript:id)
  out = out.replace(
    /\\(\[)([^\]\n]+)\\(\])\\?(\()(#transcript:[^)\n\\]+)\\?(\))/g,
    '$1$2$3$4$5$6',
  );

  // 3. Collapse a stray newline between the link text and the href:
  //    [text]\n(#transcript:id) → [text](#transcript:id)
  out = out.replace(
    /(\[[^\]\n]+\])\s*\n+\s*(\(#transcript:[^)\n]+\))/g,
    '$1$2',
  );

  // 4. Reference-style with a bubble id and no matching definition:
  //    [text][bubble-id] → [text](#transcript:bubble-id)
  out = out.replace(
    /(\[[^\]\n]+\])\[(bubble-[^\]\n]+)\]/g,
    '$1(#transcript:$2)',
  );

  // 5. Bare transcript-prefix that leaked from the prompt context:
  //    [bubble:bubble-id] → [↪](#transcript:bubble-id) (small jump-to chip)
  out = out.replace(
    /\[bubble:(bubble-[^\]\n]+)\]/g,
    '[↪](#transcript:$1)',
  );

  // 6. A destination holding whitespace (an id persisted before bubble ids were
  //    URL-safe: `bubble-Officer Kelly__off_8xlk-<ms>`) is not a CommonMark
  //    destination and would stay raw text. The angle-bracket form allows it:
  //    [text](#transcript:bubble-A B-1) → [text](<#transcript:bubble-A B-1>)
  //    Only that id shape (bubble-…-<millis>, no quotes or parentheses) is
  //    rewritten, so a link title — (#transcript:bubble-1 "title") — is never
  //    folded into the destination.
  out = out.replace(
    /\]\(#transcript:(bubble-[^()<>"'\n]*\s[^()<>"'\n]*-\d+)\)/g,
    '](<#transcript:$1>)',
  );

  return out;
}

/** Backslash-escape every ASCII punctuation character markdown could interpret. */
function escapeMarkdownText(text: string): string {
  return text.replace(/[\\`*_{}[\]()#+\-.!|<>~&]/g, '\\$&');
}

/**
 * Markdown source for an in-page citation chip: `[label](#citation:key "title")`.
 * The shared renderer turns it into `<a data-citation="key" class="app-markdown-citation">`
 * and <Markdown onCitationClick> receives `key` on click.
 *
 * A key outside the safe alphabet is a caller bug: the label is returned as
 * plain (escaped) text so it stays visible, and the event is logged at ERROR.
 */
export function markdownCitationLink(label: string, key: string, title?: string): string {
  const text = escapeMarkdownText(label);
  if (!CITATION_KEY_RE.test(key)) {
    logger.error('Markdown', 'Citation key outside the safe alphabet — rendering the label as plain text', { key });
    return text;
  }
  // Link titles cannot span lines; the title is a tooltip copy of text shown elsewhere.
  const t = title ? ` "${title.replace(/\s*\n\s*/g, ' ').replace(/[\\"]/g, '\\$&')}"` : '';
  return `[${text}](${CITATION_HREF_PREFIX}${key}${t})`;
}

/**
 * The escaped renderer output — exactly what the server path returns.
 * Exported for tests (server/client parity); callers use renderSafeMarkdownHtml.
 */
export function renderMarkdownToHtml(content: string): string {
  if (!content) return '';
  return md.parse(preprocessAnderraCitations(String(content))) as string;
}

function describeRemoved(entry: unknown): string {
  const e = entry as { element?: Element; attribute?: Attr | null; from?: Element };
  if (e.attribute) return `attribute ${e.attribute.name} on <${e.from?.nodeName?.toLowerCase() ?? '?'}>`;
  if (e.element) return `element <${e.element.nodeName?.toLowerCase() ?? '?'}>`;
  return 'unknown';
}

/**
 * Defense in depth. No DOM (server) → the renderer output unchanged. With a DOM,
 * DOMPurify checks it; if it removed nothing, the ORIGINAL string is returned
 * (identical to the server's, so hydration matches). If it removed anything,
 * the renderer's escaping failed — use the sanitized output and log at ERROR.
 */
export function finalizeRenderedHtml(html: string): string {
  if (!html || typeof window === 'undefined' || !DOMPurify.isSupported) return html;
  const purified = DOMPurify.sanitize(html, { ADD_ATTR: ['target'] }) as unknown as string;
  const removed = DOMPurify.removed;
  if (!removed || removed.length === 0) return html;
  logger.error(
    'Markdown',
    'Defense-in-depth sanitizer removed markup the markdown renderer emitted — the renderer escaping failed; showing the sanitized output',
    { removed: removed.map(describeRemoved) },
  );
  return purified;
}

/** Render untrusted markdown to safe HTML (whole, never truncated). */
export function renderSafeMarkdownHtml(content: string): string {
  return finalizeRenderedHtml(renderMarkdownToHtml(content));
}
